import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fmtNumber, StatusTag } from "../../components/format";
import { api, type ApiError } from "../../services/api";
import { useAuth } from "../../services/auth";

/**
 * ⚠ 只有 `active` 与 `maintenance` 能从界面上设置。
 *
 * `archived` 是不可逆的（历史会话仍引用该入口，而解析再也找不到它，
 * 在线会话也会被判成凭据失效），与「临时停一下」放在同一颗按钮后面迟早点错。
 * 服务端的 zod 只收这两个值，这里跟它保持一致。
 */
type ProductStatus = "active" | "maintenance";

interface SiblingRow {
  id: string;
  key: string;
  displayName: string;
  entryKind: string;
}

interface ProductRow {
  id: string;
  key: string;
  displayName: string;
  entryKind: string;
  wechatAppId: string | null;
  webHost: string | null;
  isDefault: boolean;
  /** 读侧还会返回 `archived`，所以这里比可写的那个联合宽一格。 */
  status: ProductStatus | "archived";
  maintenanceNote: string | null;
  defaultCharacterId: string | null;
  defaultCharacter: { key: string; displayName: string } | null;
  createdAt: string | null;
  _count: { conversations: number; sessions: number };
}

interface AiDisclosureStatus {
  enabled: boolean;
  updatedAt: string | null;
}

interface MiniappKnowledgeQaStatus {
  enabled: boolean;
  bondThreshold: number;
  bondThresholdMax: number;
  updatedAt: string | null;
}

/**
 * 产品入口状态页。只改 `status` 与 `maintenanceNote`。
 *
 * `key` / `entryKind` / `wechatAppId` / `webHost` / `defaultCharacterId`
 * 决定「从这个入口进来会跟谁说话」，服务端不接受这些字段的修改。
 */
export function ProductsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { message } = AntApp.useApp();
  const editable = can("product.write");
  const [target, setTarget] = useState<ProductRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["products"],
    queryFn: () => api.get<ProductRow[]>("/api/v1/admin/characters/products"),
  });

  const save = useMutation({
    mutationFn: (vars: { id: string; status: ProductStatus; note: string | null; reason: string }) =>
      api.patch<{ status: ProductStatus; siblings: SiblingRow[] }>(
        `/api/v1/admin/characters/products/${vars.id}/status`,
        { status: vars.status, note: vars.note, reason: vars.reason },
      ),
    onSuccess: (result) => {
      setTarget(null);
      void qc.invalidateQueries({ queryKey: ["products"] });

      if (result.status === "maintenance" && result.siblings.length > 0) {
        const names = result.siblings.map((s) => `${s.displayName}（${s.key}）`).join("、");
        message.warning({
          content: `同一个角色下还有 ${result.siblings.length} 个入口开着：${names}。它们共用同一批数据，要一起停就逐个改。`,
          duration: 10,
        });
      } else {
        message.success(result.status === "maintenance" ? "已设为维护中" : "已恢复");
      }
    },
    onError: (error: Error) => {
      const e = error as ApiError;
      message.error({
        content: e.requestId ? `${e.message}（${e.code}，请求 ${e.requestId}）` : e.message,
        duration: 8,
      });
    },
  });

  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <MiniappKnowledgeQaControl editable={editable} />
      <AiDisclosureControl editable={editable} />
      <Card title="产品状态" styles={{ body: { padding: 12 } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        每个 Product 是一个用户入口。状态切换会立即影响该入口的新请求；客户端不能自行声明
        characterId 或 productKey。
      </Typography.Paragraph>
      <Typography.Paragraph type="warning" style={{ fontSize: 12 }}>
        「维护中」挡的是<b>用户从这个入口进来</b>，不是数据冻结。同角色的其他入口、
        job worker 的记忆任务、QQ 通道都不受它影响。
      </Typography.Paragraph>
      {editable ? null : (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="你没有 product.write 权限，只能查看" />
      )}
      <Table<ProductRow>
        size="small"
        rowKey="id"
        loading={isLoading}
        pagination={false}
        dataSource={data ?? []}
        columns={[
          { title: "key", dataIndex: "key", width: 180 },
          { title: "名称", dataIndex: "displayName", width: 160 },
          { title: "入口类型", dataIndex: "entryKind", width: 150 },
          { title: "微信 AppId", dataIndex: "wechatAppId", render: (v) => v || "—" },
          { title: "域名", dataIndex: "webHost", render: (v) => v || "—" },
          {
            title: "默认角色",
            dataIndex: "defaultCharacter",
            width: 120,
            render: (v: { displayName: string } | null) => v?.displayName ?? "—",
          },
          {
            title: "兜底入口",
            dataIndex: "isDefault",
            width: 90,
            render: (v: boolean) => (v ? <StatusTag value="active" /> : "—"),
          },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
          {
            title: "维护说明",
            dataIndex: "maintenanceNote",
            width: 200,
            render: (v: string | null) => v || "—",
          },
          {
            title: "会话",
            dataIndex: "_count",
            width: 90,
            align: "right",
            render: (v: { conversations: number }) => fmtNumber(v?.conversations),
          },
          {
            title: "操作",
            width: 110,
            fixed: "right",
            render: (_, row) =>
              row.status === "archived" ? (
                "—"
              ) : (
                <Button size="small" type="link" disabled={!editable} onClick={() => setTarget(row)}>
                  {row.status === "maintenance" ? "恢复启用" : "设为维护中"}
                </Button>
              ),
          },
        ]}
        scroll={{ x: 1500 }}
      />
      {target ? <StatusModal row={target} onClose={() => setTarget(null)} save={save} /> : null}
      </Card>
    </Space>
  );
}

function MiniappKnowledgeQaControl({ editable }: { editable: boolean }) {
  const qc = useQueryClient();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<{ bondThreshold: number; confirmation: string; reason: string }>();
  const [pending, setPending] = useState<{
    enabled: boolean;
    mode: "toggle" | "threshold";
    before: MiniappKnowledgeQaStatus;
  } | null>(null);
  const draftThreshold = Form.useWatch("bondThreshold", form);

  const query = useQuery({
    queryKey: ["miniappKnowledgeQaControl"],
    queryFn: () =>
      api.get<MiniappKnowledgeQaStatus>(
        "/api/v1/admin/characters/products/miniapp-knowledge-qa",
      ),
  });
  const mutation = useMutation({
    mutationFn: (input: {
      enabled: boolean;
      bondThreshold: number;
      confirmation: string;
      reason: string;
      expectedUpdatedAt: string | null;
    }) =>
      api.patch<MiniappKnowledgeQaStatus>(
        "/api/v1/admin/characters/products/miniapp-knowledge-qa",
        input,
      ),
    onSuccess: (data) => {
      qc.setQueryData(["miniappKnowledgeQaControl"], data);
    },
  });

  const status = query.data;
  const confirmationText = pending?.mode === "threshold"
    ? "修改资料查询羁绊门槛"
    : pending?.enabled ? "开启小程序和网页资料查询首页" : "关闭小程序和网页资料查询首页";

  const begin = (enabled: boolean, mode: "toggle" | "threshold") => {
    if (!status) return;
    // 固定打开弹窗时的版本；查询自动刷新不能替换乐观锁，覆盖其他管理员的修改。
    setPending({ enabled, mode, before: status });
    form.resetFields();
    form.setFieldsValue({ bondThreshold: status.bondThreshold });
  };

  const close = () => {
    if (!mutation.isPending) setPending(null);
  };
  const submit = async () => {
    if (!pending) return;
    try {
      const values = await form.validateFields();
      await mutation.mutateAsync({
        enabled: pending.enabled,
        bondThreshold: values.bondThreshold,
        confirmation: values.confirmation,
        reason: values.reason,
        expectedUpdatedAt: pending.before.updatedAt,
      });
      message.success("资料查询首页设置已保存");
      setPending(null);
    } catch (cause) {
      if (cause instanceof Error) message.error(cause.message);
      if ((cause as ApiError)?.code === "MINIAPP_KNOWLEDGE_QA_STALE") {
        setPending(null);
        void qc.invalidateQueries({ queryKey: ["miniappKnowledgeQaControl"] });
      }
    }
  };

  if (query.error) {
    return <Alert type="error" showIcon message={(query.error as Error).message} />;
  }

  return (
    <>
      <Card
        title="小程序和网页资料查询首页（原知识问答）"
        loading={query.isLoading}
        extra={status ? (
          <Space>
            {!editable ? <Tag>只读</Tag> : null}
            <Switch
              checked={status.enabled}
              checkedChildren="已开启"
              unCheckedChildren="已关闭"
              disabled={!editable || mutation.isPending || query.isFetching}
              onChange={(enabled) => begin(enabled, "toggle")}
            />
          </Space>
        ) : null}
      >
        <Alert
          type={status?.enabled ? "success" : "info"}
          showIcon
          message={
            status?.enabled
              ? status.bondThreshold === 0
                ? "开关已开启，门槛为 0：当前不分流用户"
                : `羁绊不足 ${status.bondThreshold} 且未升到 2 级的小程序和网页用户正在进入造梦体资料查询`
              : "小程序和网页首页正在进入原有角色聊天"
          }
          description={
            status?.enabled
              ? `影响 miniapp_shell 与普通 web 首页。未达门槛用户进入 /information，只做已发布资料查询；其余前端路径会转回资料页。羁绊达到 ${status.bondThreshold}，或已经升到 2 级及以上的用户仍进入原角色聊天；微信服务号首页不受影响。此开关不等同于后端角色能力的停用。`
              : "开启后的目标页面为 /information（造梦体资料查询），旧 /knowledge 地址也会转入该页。关闭时小程序和普通网页用户均继续进入原角色聊天；微信服务号首页不受此开关影响。"
          }
        />
        {status ? (
          <Space wrap style={{ marginTop: 16 }}>
            <Typography.Text>当前羁绊门槛：{fmtNumber(status.bondThreshold)}</Typography.Text>
            <Button
              disabled={!editable || mutation.isPending || query.isFetching}
              onClick={() => begin(status.enabled, "threshold")}
            >修改羁绊门槛</Button>
            <Typography.Text type="secondary">2 级及以上用户始终不受影响；门槛为 0 时不分流。</Typography.Text>
          </Space>
        ) : null}
      </Card>

      <Modal
        title={confirmationText}
        open={pending !== null}
        okText={pending?.mode === "threshold" ? "保存门槛" : pending?.enabled ? "确认开启" : "确认关闭"}
        okButtonProps={{ danger: pending?.mode === "toggle" && !pending.enabled }}
        confirmLoading={mutation.isPending}
        onCancel={close}
        onOk={() => void submit()}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          message="保存后影响小程序和普通网页下一次加载首页"
          description={`开启后的目标页面为 /information（造梦体资料查询）。开启后只覆盖羁绊不足 ${draftThreshold ?? pending?.before.bondThreshold ?? "—"} 且尚未升到 2 级的用户；门槛为 0 时不分流。已经打开页面的用户需要刷新才能同步首页落点，不会中断正在进行的角色对话。`}
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="bondThreshold"
            label="羁绊门槛"
            extra={`可设置 0～${pending?.before.bondThresholdMax ?? "—"} 的整数。达到门槛或已升到 2 级的用户保留原角色聊天；关闭模式时也可以提前保存门槛。`}
            rules={[{ required: true, type: "integer", min: 0, max: pending?.before.bondThresholdMax, message: "请输入允许范围内的整数" }]}
          >
            <InputNumber min={0} max={pending?.before.bondThresholdMax} step={1} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="操作原因"
            rules={[{ required: true, min: 4, max: 200, message: "请填写至少 4 个字的操作原因" }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="confirmation"
            label={`请输入“${confirmationText}”确认`}
            rules={[
              { required: true },
              {
                validator: (_, value) =>
                  value === confirmationText
                    ? Promise.resolve()
                    : Promise.reject(new Error("确认文字不匹配")),
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function AiDisclosureControl({ editable }: { editable: boolean }) {
  const qc = useQueryClient();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<{ confirmation: string; reason: string }>();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  const query = useQuery({
    queryKey: ["aiDisclosureControl"],
    queryFn: () =>
      api.get<AiDisclosureStatus>(
        "/api/v1/admin/characters/products/ai-disclosure",
      ),
  });
  const mutation = useMutation({
    mutationFn: (input: {
      enabled: boolean;
      confirmation: string;
      reason: string;
      expectedUpdatedAt: string | null;
    }) =>
      api.patch<AiDisclosureStatus>(
        "/api/v1/admin/characters/products/ai-disclosure",
        input,
      ),
    onSuccess: (data) => {
      qc.setQueryData(["aiDisclosureControl"], data);
    },
  });

  const status = query.data;
  const confirmationText = pendingEnabled
    ? "开启AI生成标识"
    : "关闭AI生成标识";

  const close = () => {
    if (!mutation.isPending) setPendingEnabled(null);
  };
  const submit = async () => {
    if (pendingEnabled === null || !status) return;
    try {
      const values = await form.validateFields();
      await mutation.mutateAsync({
        enabled: pendingEnabled,
        confirmation: values.confirmation,
        reason: values.reason,
        expectedUpdatedAt: status.updatedAt,
      });
      message.success(pendingEnabled ? "AI 生成标识已开启" : "AI 生成标识已关闭");
      setPendingEnabled(null);
    } catch (cause) {
      if (cause instanceof Error) message.error(cause.message);
    }
  };

  if (query.error) {
    return <Alert type="error" showIcon message={(query.error as Error).message} />;
  }

  return (
    <>
      <Card
        title="AI 生成内容标识"
        loading={query.isLoading}
        extra={status ? (
          <Space>
            {!editable ? <Tag>只读</Tag> : null}
            <Switch
              checked={status.enabled}
              checkedChildren="已开启"
              unCheckedChildren="已关闭"
              disabled={!editable || mutation.isPending}
              onChange={(enabled) => {
                form.resetFields();
                setPendingEnabled(enabled);
              }}
            />
          </Space>
        ) : null}
      >
        <Alert
          type={status?.enabled ? "success" : "warning"}
          showIcon
          message={
            status?.enabled
              ? "模型回复底部正在显示“AI生成内容”标识"
              : "AI 生成标识已关闭"
          }
          description="这是小程序深度合成审核要求。审核和正式运营期间请保持开启；关闭会让网页端和小程序壳在下次刷新后隐藏标识。"
        />
      </Card>

      <Modal
        title={confirmationText}
        open={pendingEnabled !== null}
        okText={pendingEnabled ? "确认开启" : "确认关闭"}
        okButtonProps={{ danger: pendingEnabled === false }}
        confirmLoading={mutation.isPending}
        onCancel={close}
        onOk={() => void submit()}
        destroyOnHidden
      >
        <Alert
          type={pendingEnabled ? "info" : "warning"}
          showIcon
          message={
            pendingEnabled
              ? "保存后，用户刷新页面即可看到 AI 生成标识。"
              : "关闭后，用户将看不到 AI 生成标识，可能导致小程序审核或合规风险。"
          }
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="操作原因"
            rules={[{ required: true, min: 4, max: 200, message: "请填写至少 4 个字的操作原因" }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="confirmation"
            label={`请输入“${confirmationText}”确认`}
            rules={[
              { required: true },
              {
                validator: (_, value) =>
                  value === confirmationText
                    ? Promise.resolve()
                    : Promise.reject(new Error("确认文字不匹配")),
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function StatusModal({
  row,
  onClose,
  save,
}: {
  row: ProductRow;
  onClose: () => void;
  save: {
    isPending: boolean;
    mutate: (v: { id: string; status: ProductStatus; note: string | null; reason: string }) => void;
  };
}) {
  const [form] = Form.useForm<{ note?: string; reason: string }>();
  const next: ProductStatus = row.status === "maintenance" ? "active" : "maintenance";

  return (
    <Modal
      open
      title={next === "maintenance" ? `把「${row.displayName}」设为维护中` : `恢复「${row.displayName}」`}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={save.isPending}
      okText={next === "maintenance" ? "设为维护中" : "恢复启用"}
      okButtonProps={next === "maintenance" ? { danger: true } : {}}
    >
      <Alert
        type={next === "maintenance" ? "warning" : "info"}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          next === "maintenance"
            ? "所有非管理员从这个入口进来都会看到维护页，已登录的人下一次请求就生效。有管理角色的账号不受影响，可以照常进去测。"
            : "恢复之后维护说明会被清空，用户可以立即重新进入。"
        }
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ note: row.maintenanceNote ?? "" }}
        onFinish={(v) =>
          save.mutate({
            id: row.id,
            status: next,
            note: next === "maintenance" ? (v.note?.trim() || null) : null,
            reason: v.reason.trim(),
          })
        }
      >
        {next === "maintenance" ? (
          <Form.Item
            name="note"
            label="给用户看的话"
            extra="留空就使用维护页的默认文案。"
          >
            <Input.TextArea rows={2} maxLength={200} showCount placeholder="预计 03:00 恢复" />
          </Form.Item>
        ) : null}
        <Form.Item
          name="reason"
          label="变更理由"
          rules={[
            { required: true, whitespace: true, message: "写操作必须填理由，会进入审计日志" },
            { max: 200, message: "变更理由不能超过 200 字" },
          ]}
        >
          <Input.TextArea rows={2} maxLength={200} showCount placeholder="为什么现在停 / 为什么现在恢复" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
